import React, { useEffect, useState } from "react";
import { Modal, ModalBody, Form, Label, Spinner } from "reactstrap";
import { Icon, Col, Button } from "../../../components/Component";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { addForumSuccess, updateForumSuccess } from "../../../slice/forums";
import { addForum, updateForum } from "../../../api/forum";
import { uploadFile } from "../../../api/upload-file";

// Forum.name / desc / image are LocalizedText { en, ar? } subdocs. Legacy
// data may still be plain strings until the BE migration runs.
const readEn = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.en ?? "";
};
const readAr = (v) => {
  if (!v || typeof v === "string") return "";
  return v.ar ?? "";
};

const buildLocalized = (en, ar, { required } = { required: false }) => {
  const enTrim = (en ?? "").trim();
  const arTrim = (ar ?? "").trim();
  if (!enTrim && !required) return undefined;
  return arTrim ? { en: enTrim, ar: arTrim } : { en: enTrim };
};

const UpdateForumModal = ({ isOpen, closeModal, forum }) => {
  const schema = yup
    .object({
      nameEn: yup.string().trim().required("Name (English) is required"),
      nameAr: yup.string().trim().nullable(),
      descEn: yup.string().trim().nullable(),
      descAr: yup.string().trim().nullable(),
      imageEn: yup.string().required("Image (English) is required"),
      imageAr: yup.string().nullable(),
    })
    .required();
  const {
    watch,
    setValue,
    reset,
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
  });
  const imageEn = watch("imageEn");
  const imageAr = watch("imageAr");

  const onSubmit = async (data) => {
    const payload = {
      name: buildLocalized(data.nameEn, data.nameAr, { required: true }),
      desc: buildLocalized(data.descEn, data.descAr),
      image: buildLocalized(data.imageEn, data.imageAr, { required: true }),
    };
    if (forum) {
      await onUpdateForum(payload);
    } else {
      await onAddForum(payload);
    }
  };

  useEffect(() => {
    reset({
      nameEn: readEn(forum?.name),
      nameAr: readAr(forum?.name),
      descEn: readEn(forum?.desc),
      descAr: readAr(forum?.desc),
      imageEn: readEn(forum?.image),
      imageAr: readAr(forum?.image),
    });
  }, [reset, forum]);

  ///
  const dispatch = useDispatch();
  const [loadingImageField, setLoadingImageField] = useState(null);
  const uploadImage = async (formData, field) => {
    setLoadingImageField(field);
    try {
      const res = await uploadFile(formData);
      setValue(field, res.fileUrl);
    } catch (error) {
      toast.error(error);
    }
    setLoadingImageField(null);
  };
  const onAddForum = async (data) => {
    try {
      const res = await addForum(data);
      dispatch(addForumSuccess(res));
      toast.success("Add forum successfully");
      closeModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const onUpdateForum = async (data) => {
    try {
      const res = await updateForum(forum._id, data);
      dispatch(updateForumSuccess(res));
      toast.success("Update forum successfully");
      closeModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const renderImageUpload = (field, value) => (
    <div className="form-control-wrap">
      <input
        type="file"
        id={`${field}-input`}
        multiple={false}
        hidden
        accept="image/png, image/gif, image/jpeg"
        onChange={(e) => {
          if (e.target.files) {
            const file = e.target.files[0];
            const data = new FormData();
            data.append("image", file);
            uploadImage(data, field);
          }
        }}
      />
      <label htmlFor={`${field}-input`}>
        {loadingImageField === field ? (
          <div className="w-150px h-150px d-flex align-items-center justify-content-center border">
            <Spinner />
          </div>
        ) : (
          <div className="w-150px h-150px d-flex align-items-center justify-content-center border">
            {value ? (
              <img
                src={value}
                width={150}
                height={150}
                alt="image"
                style={{
                  objectFit: "cover",
                }}
              />
            ) : (
              <Icon
                name="upload"
                style={{
                  fontSize: "30px",
                }}
              />
            )}
          </div>
        )}
      </label>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => closeModal()}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            closeModal();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">{forum != null ? "Update" : "Add"} Forum</h5>
          <div className="mt-4">
            <Form
              className="row gy-4 form-validate"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Name (English)</label>
                  <div className="form-control-wrap">
                    <input
                      className="form-control"
                      type="text"
                      {...register("nameEn")}
                      placeholder="Enter name in English"
                    />
                    {errors.nameEn && (
                      <span className="invalid">{errors.nameEn.message}</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Name (Arabic)</label>
                  <div className="form-control-wrap">
                    <input
                      className="form-control"
                      type="text"
                      dir="rtl"
                      {...register("nameAr")}
                      placeholder="أدخل الاسم بالعربية"
                    />
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div className="form-group">
                  <Label className="form-label" htmlFor="fv-desc-en">
                    Description (English)
                  </Label>
                  <div className="form-control-wrap">
                    <input
                      id="fv-desc-en"
                      className="form-control"
                      type="text"
                      {...register("descEn")}
                      placeholder="Enter description in English"
                    />
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div className="form-group">
                  <Label className="form-label" htmlFor="fv-desc-ar">
                    Description (Arabic)
                  </Label>
                  <div className="form-control-wrap">
                    <input
                      id="fv-desc-ar"
                      className="form-control"
                      type="text"
                      dir="rtl"
                      {...register("descAr")}
                      placeholder="أدخل الوصف بالعربية"
                    />
                  </div>
                </div>
              </Col>
              <Col sm="6">
                <div className="form-group">
                  <label className="form-label">Image (English)</label>
                  {renderImageUpload("imageEn", imageEn)}
                  {errors.imageEn && !imageEn && (
                    <span className="invalid-message">
                      {errors.imageEn.message}
                    </span>
                  )}
                </div>
              </Col>
              <Col sm="6">
                <div className="form-group">
                  <label className="form-label">Image (Arabic, optional)</label>
                  {renderImageUpload("imageAr", imageAr)}
                </div>
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button color="primary" size="md" type="submit">
                      {isSubmitting ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};
export default UpdateForumModal;
